import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { PlanService } from '../../../core/services/plan.service';
import { GetCreditCardIconSrcPipe } from '../../pipes/card-icon-src.pipe';
import { PaymentTextPipe } from '../../pipes/payment-text.pipe';
import { CurrencyBRLPipe } from '../../pipes/currency-brl.pipe';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { EXCLUDED_VARIATION_UUIDS } from '../../constants/variation-uuids.constant';
import {
  isRegistryDomainProductUuid,
  isRegistryDomainVariationUuid,
} from '../../../core/utils/cart-domain-suggestion-rules';

export interface StatusIcon {
  class: string;
  color: string;
  label: string;
}

export interface PlanPanel {
  uuid: string;
  productName: string;
  variationName?: string;
  productUuid?: string;
  parentProductUuid?: string;
  quantity: number;
  price: number;
  priceMonthly?: number;
  status?: 'ativo' | 'inativo' | 'suspenso' | null;
  statusIcon: StatusIcon;
  contractDate?: string;
  creationDate?: string;
  expirationDate?: any;
  period?: string;
  showDetailsButton?: boolean;
  contractPeriod?: string;
  paymentMethod?: string;
  cardType?: string;
  cardLastFourDigits?: string;
  flow?: 'MOVEL' | 'BOLETO_CARTAO';
}

@Component({
  selector: 'umkt-plan-panel',
  standalone: true,
  imports: [CommonModule, GetCreditCardIconSrcPipe, PaymentTextPipe, CurrencyBRLPipe, I18nPipe],
  templateUrl: './plan-panel.component.html',
  styleUrls: ['./plan-panel.component.scss'],
})
export class PlanPanelComponent implements OnInit, OnChanges {
  @Input() plans: PlanPanel[] = [];
  @Input() defaultOpen: number = -1;
  @Input() preserveOpenState: boolean = false;
  
  @Output() planDetails = new EventEmitter<string>();
  @Output() managePlan = new EventEmitter<string>();

  openedIndexes: number[] = [];
  private openedIdentifiers: Set<string> = new Set<string>();
  private router = inject(Router);
  private planService = inject(PlanService);
  private i18nService = inject(I18nService);


  ngOnInit() {
    this.initializeOpenedPanels();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['plans'] && !changes['plans'].firstChange) {
      if (this.preserveOpenState) {
        this.preserveOpenedPanelsState();
      } else {
        this.openedIndexes = [];
        this.openedIdentifiers.clear();
        this.initializeOpenedPanels();
      }
    }
  }

  private preserveOpenedPanelsState() {
    const newOpenedIndexes: number[] = [];
    
    this.plans.forEach((plan, index) => {
      if (this.openedIdentifiers.has(plan.uuid)) {
        newOpenedIndexes.push(index);
      }
    });

    this.openedIndexes = newOpenedIndexes;
  }

  private initializeOpenedPanels() {
    if (this.defaultOpen >= 0 && this.defaultOpen < this.plans.length) {
      this.openedIndexes = [this.defaultOpen];
      if (this.plans[this.defaultOpen]) {
        this.openedIdentifiers.add(this.plans[this.defaultOpen].uuid);
      }
    }
  }

  togglePanel(index: number) {
    const currentIndex = this.openedIndexes.indexOf(index);
    const plan = this.plans[index];
    
    if (currentIndex === -1) {
      this.openedIndexes.push(index);
      if (plan) {
        this.openedIdentifiers.add(plan.uuid);
      }
    } else {
      this.openedIndexes.splice(currentIndex, 1);
      if (plan) {
        this.openedIdentifiers.delete(plan.uuid);
      }
    }
  }

  isPanelOpen(index: number): boolean {
    return this.openedIndexes.includes(index);
  }

  getStatusClass(status: string | null | undefined): string {
     if (!status) {
    return 'status-default';
  }
  
    switch (status.toLowerCase()) {
      case 'ativo':
        return 'status-active';
      case 'inativo':
        return 'status-inactive';
      case 'suspenso':
      case 'suspended':
        return 'status-suspended';
      default:
        return 'status-default';
    }
  }
  
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  getFormattedPlanName(plan: PlanPanel): string {
  if (!plan) {
    return '-';
  }

  const hasProduct = !!plan.productName;
  const hasVariation = !!plan.variationName;

  if (hasProduct && hasVariation) {
    return `${plan.productName} - ${plan.variationName}`;
  }

  if (hasProduct) {
    return plan.productName;
  }

  return '-';
}


  getStatusText(status: string | null | undefined): string {
    if (!status) {
    return '-';
  }
    switch (status.toLowerCase()) {
      case 'ativo':
        return this.i18nService.instant('components.planPanel.status.active');
      case 'inativo':
        return this.i18nService.instant('components.planPanel.status.inactive');
      case 'suspenso':
      case 'suspended':
        return this.i18nService.instant('components.planPanel.status.suspended');
      default:
        return this.i18nService.instant('components.planPanel.status.default');
    }
  }

  static getStatusIcon(status: string): StatusIcon {
    const normalized = status.toLowerCase();
    
    switch (normalized) {
      case 'active':
      case 'ativo':
        return {
          class: 'mdn-Icon-check-circulo mdn-Icon--sm',
          color: 'var(--color-success-700-to-white)',
          label: 'check-circulo'
        };
      case 'inactive':
      case 'inativo':
        return {
          class: 'mdn-Icon-circulo-exclamacao mdn-Icon--sm',
          color: 'var(--color-red-300-to-white)',
          label: 'circulo-exclamacao'
        };
      case 'suspended':
      case 'suspenso':
        return {
          class: 'mdn-Icon-pause-vazio-circulo mdn-Icon--sm',
          color: 'var(--color-neutral-700-to-white)',
          label: 'pause-vazio-circulo'
        };
      default:
        return {
          class: 'mdn-Icon-circulo-exclamacao mdn-Icon--sm',
          color: 'var(--color-cool-gray-50-to-white)',
          label: 'circulo-exclamacao'
        };
    }
  }

  onButtonClick(plan: PlanPanel) {
    if (plan.status === 'ativo') {
      this.managePlan.emit(plan.uuid);
    } else {
      this.planDetails.emit(plan.uuid);
    }
  }

  onChangePaymentMethod(plan: PlanPanel): void {
    this.planService.setSelectedPlanId(plan.uuid);
    this.router.navigate(['/my-account/plans/details', plan.uuid, 'change-payment']);
  }

  onPlanDetails(planUuid: string): void {
    this.planDetails.emit(planUuid);
  }

  isRegistryDomainPlan(plan: PlanPanel): boolean {
    return (
      isRegistryDomainProductUuid(plan.productUuid) ||
      isRegistryDomainProductUuid(plan.parentProductUuid) ||
      isRegistryDomainVariationUuid(plan.productUuid)
    );
  }

  shouldDivideBy12(plan: PlanPanel): boolean {
    if (this.isRegistryDomainPlan(plan)) {
      return false;
    }
    if (!plan.productUuid) {
      return true;
    }
    return !EXCLUDED_VARIATION_UUIDS.includes(plan.productUuid);
  }

  getMonthlyPrice(plan: PlanPanel): number {
    return this.shouldDivideBy12(plan) ? plan.price / 12 : plan.price;
  }

  getPriceLabelKey(plan: PlanPanel): string {
    return this.isRegistryDomainPlan(plan)
      ? 'components.planPanel.annualValue'
      : 'components.planPanel.monthlyValue';
  }

  shouldShowYearlySuffix(plan: PlanPanel): boolean {
    if (this.isRegistryDomainPlan(plan)) {
      return false;
    }
    if (!plan.productUuid) {
      return true;
    }
    return !EXCLUDED_VARIATION_UUIDS.includes(plan.productUuid);
  }

  getYearlySuffix(plan: PlanPanel): string {
    return this.shouldShowYearlySuffix(plan) ? this.i18nService.instant('components.planPanel.perYear') : '';
  }
}